<template>
  <div :class="$style.container">
    <!-- Header -->
    <v-card :class="$style.headerCard">
      <div :class="$style.headerWrapper">
        <div :class="$style.searchWrapper">
          <v-text-field
            v-model="queryParams.search"
            hide-details
            density="compact"
            append-inner-icon="fas fa-solid fa-search"
            clearable
            :placeholder="globalTranslate('externalLmsClientKeys.searchPlaceholder') || globalTranslate('search')"
            @update:model-value="val => onSearchChange(val)"
          />
        </div>
        <v-btn
          color="primary"
          @click="openCreateDialog"
        >
          <v-icon left icon="mdi:mdi-plus" />
          {{ globalTranslate('externalLmsClientKeys.createButton') }}
        </v-btn>
      </div>
    </v-card>

    <!-- Data Table -->
    <app-data-table-server
      :headers="headers"
      :items="items"
      :total-result="totalResult"
      :is-items-loading="isLoading"
      :page="queryParams.page"
      :per-page="queryParams.perPage"
      :sort="currentSort"
      :total-pages="totalPages"
      :is-checkbox-shown="false"
      :is-expandable="false"
      :can-row-toggle="false"
      @on-page-change="onPageChange"
      @update:sort-by="onSortChange"
    >
      <template #name="{ item }">
        <div :class="$style.nameColumn">
          {{ item.raw.name }}
        </div>
      </template>

      <template #client_key="{ item }">
        <code :class="$style.clientKeyCode">{{ item.raw.client_key }}</code>
      </template>

      <template #active="{ item }">
        <v-chip
          :color="item.raw.active === 'Y' ? 'success' : 'grey'"
          size="small"
          label
        >
          {{ item.raw.active }}
        </v-chip>
      </template>

      <template #domains="{ item }">
        <div>
          {{ Array.isArray(item.raw.domains) ? item.raw.domains.join(', ') : '-' }}
        </div>
      </template>

      <template #course_ids="{ item }">
        <course-multiple-lookup
          :model-value="getResolvedCourses(item.raw.course_ids)"
          disabled
          :class="$style.courseLookupView"
        />
      </template>

      <template #create_date="{ item }">
        <div class="text-nowrap">
          {{ formatDate(item.raw.create_date) }}
        </div>
      </template>

      <template #actions="{ item }">
        <v-btn
          size="small"
          color="primary"
          variant="outlined"
          @click="openEditDialog(item.raw)"
        >
          {{ globalTranslate('button.edit') }}
        </v-btn>
        <v-btn
          size="small"
          color="success"
          variant="flat"
          class="ml-2"
          @click="downloadScorm(item.raw)"
        >
          {{ globalTranslate('button.download') }}
        </v-btn>
      </template>
    </app-data-table-server>

    <!-- Create/Edit Dialog -->
    <v-dialog
      v-model="isDialogOpen"
      max-width="650"
    >
      <v-card :class="$style.modalCard">
        <v-card-title :class="$style.modalHeader">
          {{ isEditMode ? globalTranslate('externalLmsClientKeys.editHeader') : globalTranslate('externalLmsClientKeys.createButton') }}
        </v-card-title>
        <v-card-text class="pa-6">
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="form.name"
                :label="globalTranslate('externalLmsClientKeys.clientName') + ' *'"
                variant="outlined"
                density="compact"
                :error-messages="errors.name"
                class="mb-2"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="form.client_key"
                :label="globalTranslate('externalLmsClientKeys.clientKey') + ' *'"
                variant="outlined"
                density="compact"
                :disabled="isEditMode"
                :error-messages="errors.client_key"
                :hint="globalTranslate('externalLmsClientKeys.clientKeyHint')"
                persistent-hint
                class="mb-2"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="form.domains"
                :label="globalTranslate('externalLmsClientKeys.domains') + ' *'"
                variant="outlined"
                density="compact"
                placeholder="e.g. cloud.scorm.com, cornerstoneondemand.com"
                hint="Add multiple domains separated by comma. At least one domain is required."
                persistent-hint
                :error-messages="errors.domains"
                class="mb-2"
              />
            </v-col>
            <v-col
              v-if="isEditMode"
              cols="12"
            >
              <v-select
                v-model="form.active"
                :items="activeOptions"
                item-title="label"
                item-value="value"
                :label="globalTranslate('externalLmsClientKeys.active')"
                variant="outlined"
                density="compact"
                class="mb-2"
              />
            </v-col>
            <v-col cols="12">
              <v-label class="mb-1 d-block">
                {{ globalTranslate('externalLmsClientKeys.courseIds') }}
              </v-label>
              <course-multiple-lookup
                v-model="form.course_ids"
                :class="$style.courseLookupEdit"
                @update:model-value="onCoursesChange"
              />
              <div class="text-caption text-grey my-2 pl-3">
                {{ globalTranslate('externalLmsClientKeys.leaveEmptyForAll') }}
              </div>
            </v-col>
            <v-col
              v-if="form.course_ids.length > 0"
              cols="12"
            >
              <v-label class="mb-2 d-block">
                {{ globalTranslate('externalLmsClientKeys.pricePerRegistration') }}
              </v-label>
              <div
                v-for="course in form.course_ids"
                :key="course.id"
                :class="$style.coursePriceRow"
              >
                <span :class="$style.coursePriceLabel">{{ course.name }}</span>
                <v-text-field
                  v-model="form.course_prices[course.id]"
                  type="number"
                  min="0"
                  step="0.01"
                  prefix="$"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :class="$style.coursePriceInput"
                  placeholder="0.00"
                />
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4 bg-light">
          <v-spacer />
          <v-btn
            variant="text"
            color="grey-darken-1"
            class="px-5"
            @click="isDialogOpen = false"
          >
            {{ globalTranslate('button.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            class="px-5"
            :loading="isSubmitting"
            variant="elevated"
            @click="onSubmit"
          >
            {{ isEditMode ? globalTranslate('button.edit') : globalTranslate('button.create') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.isShowing"
      location="top right"
      :timeout="5000"
    >
      <v-alert
        closable
        :type="snackbar.type"
        :color="snackbar.type"
        @click:close="snackbar.isShowing = false"
      >
        {{ snackbar.message }}
      </v-alert>
    </v-snackbar>
  </div>
</template>

<script setup>
import {
  ref, reactive, computed, onMounted,
} from 'vue';
import axios from 'axios';
import moment from 'moment';
import { lookupService } from '@pfsc/core/services/lookup.service';
import AppDataTableServer from '../../common/shared/components/app-data-table-server.vue';
import { globalTranslate } from '../../common/shared/locale/app.i18n';
import CourseMultipleLookup from '../../common/shared/components/lookup/course-multiple-lookup.vue';
import { debounce } from '../../common/core/utils/debounce';
import { downloadFileService } from '../../common/core/ui-services/download-file.service';

// Table state
const items = ref([]);
const totalResult = ref(0);
const totalPages = ref(0);
const isLoading = ref(false);
const allCourses = ref([]);

const queryParams = reactive({
  page: 1,
  perPage: 25,
  sortBy: '',
  sortDirection: '',
  search: '',
});

const currentSort = computed(() => {
  if (!queryParams.sortBy) return [];
  return [{ key: queryParams.sortBy, order: queryParams.sortDirection }];
});

const headers = [
  {
    title: globalTranslate('externalLmsClientKeys.clientName'), key: 'name', sortable: true, minWidth: '150px',
  },
  {
    title: globalTranslate('externalLmsClientKeys.clientKey'), key: 'client_key', sortable: true, minWidth: '150px',
  },
  {
    title: globalTranslate('externalLmsClientKeys.active'), key: 'active', sortable: false, minWidth: '80px',
  },
  {
    title: globalTranslate('externalLmsClientKeys.domains'), key: 'domains', sortable: false, minWidth: '200px',
  },
  {
    title: globalTranslate('externalLmsClientKeys.courses'), key: 'course_ids', sortable: false, minWidth: '250px',
  },
  {
    title: globalTranslate('externalLmsClientKeys.createdDate'), key: 'create_date', sortable: true, minWidth: '150px',
  },
  {
    title: globalTranslate('externalLmsClientKeys.action'), key: 'actions', sortable: false, minWidth: '200px',
  },
];

const activeOptions = [
  { label: 'Y', value: 'Y' },
  { label: 'N', value: 'N' },
];

// Dialog state
const isDialogOpen = ref(false);
const isEditMode = ref(false);
const isSubmitting = ref(false);
const editingId = ref(null);

const form = reactive({
  name: '',
  client_key: '',
  course_ids: [],
  course_prices: {},
  domains: '',
  active: 'Y',
});

const errors = reactive({
  name: '',
  client_key: '',
  domains: '',
});

// Snackbar
/** @type {import('vue').UnwrapRef<{isShowing: boolean, message: string, type: 'success' | 'error' | 'info' | 'warning'}>} */
const snackbar = reactive({
  isShowing: false,
  message: '',
  /** @type {'success' | 'error' | 'info' | 'warning'} */
  type: 'success',
});

/**
 * Format date for display.
 * @param {string} date - Date string.
 * @returns {string}
 */
const formatDate = date => (date ? moment(date).format('YYYY-MM-DD HH:mm') : '-');

/**
 * Get resolved courses for lookup component
 * @param {number[]} ids - Course IDs.
 * @returns {Array}
 */
const getResolvedCourses = ids => {
  if (!ids || ids.length === 0) return [];
  return ids.map(id => allCourses.value.find(c => c.id === id)).filter(Boolean);
};

/**
 * Show notification.
 * @param {string} message Message.
 * @param {'success'|'error'} type Type.
 */
const showNotification = (message, type = 'success') => {
  snackbar.message = message;
  snackbar.type = type;
  snackbar.isShowing = true;
};

/**
 * Fetch client keys from API.
 */
const fetchClientKeys = async () => {
  isLoading.value = true;
  try {
    const response = await axios.get('/api/v2/admin/external-lms/client-keys', {
      params: {
        page: queryParams.page,
        perPage: queryParams.perPage,
        sortBy: queryParams.sortBy,
        sortDirection: queryParams.sortDirection,
        search: queryParams.search,
      },
    });

    items.value = response.data.data;
    totalResult.value = response.data.total;
    totalPages.value = response.data.totalPages;
  } catch (e) {
    showNotification(e.response?.data?.message ?? globalTranslate('externalLmsClientKeys.messages.fetchFailed'), 'error');
  } finally {
    isLoading.value = false;
  }
};

/**
 * Handle search change with debounce.
 */
const onSearchChange = debounce(() => {
  queryParams.page = 1;
  fetchClientKeys();
}, 500);

/**
 * Handle page change.
 * @param {object} options Page options.
 */
const onPageChange = options => {
  queryParams.page = options.page;
  queryParams.perPage = options.perPage;
  fetchClientKeys();
};

/**
 * Handle sort change.
 * @param {Array} sortValue Sort value.
 */
const onSortChange = sortValue => {
  if (sortValue && sortValue.length > 0) {
    queryParams.sortBy = sortValue[0].key;
    queryParams.sortDirection = sortValue[0].order;
  } else {
    queryParams.sortBy = '';
    queryParams.sortDirection = '';
  }
  queryParams.page = 1;
  fetchClientKeys();
};

/**
 * Download SCORM zips for the client.
 * @param {object} item - Row item.
 */
const downloadScorm = async item => {
  let courseIds = [];
  if (item.course_ids && item.course_ids.length > 0) {
    courseIds = item.course_ids;
  } else {
    // Download all available courses
    courseIds = allCourses.value.map(c => c.id);
  }

  if (courseIds.length === 0) {
    showNotification('No courses available to download.', 'error');
    return;
  }

  // Handle single course download
  if (courseIds.length === 1) {
    const courseId = courseIds[0];
    const url = `/api/v2/admin/external-lms/client-keys/${item.id}/download-scorm/${courseId}`;
    try {
      const response = await axios.get(url, { responseType: 'blob' });
      downloadFileService.streamDownloadCertificatesAsZip(response);
    } catch (error) {
      showNotification(`Failed to download course ${courseId}`, 'error');
    }
    return;
  }

  // Handle multi-course bundle download
  const url = `/api/v2/admin/external-lms/client-keys/${item.id}/download-scorm-bundle`;
  try {
    const response = await axios.post(url, { course_ids: courseIds }, { responseType: 'blob' });
    downloadFileService.streamDownloadCertificatesAsZip(response);
  } catch (error) {
    showNotification('Failed to download course bundle', 'error');
  }
};

/**
 * Remove prices for deselected courses.
 * @param {Array} courses - Currently selected course objects.
 */
const onCoursesChange = courses => {
  const activeIds = new Set(courses.map(c => c.id));
  Object.keys(form.course_prices).forEach(id => {
    if (!activeIds.has(Number(id))) delete form.course_prices[id];
  });
};

/**
 * Open create dialog.
 */
const openCreateDialog = () => {
  isEditMode.value = false;
  editingId.value = null;
  form.name = '';
  form.client_key = '';
  form.course_ids = [];
  form.course_prices = {};
  form.domains = '';
  form.active = 'Y';
  errors.name = '';
  errors.client_key = '';
  errors.domains = '';
  isDialogOpen.value = true;
};

/**
 * Open edit dialog.
 * @param {object} item - Row item.
 */
const openEditDialog = item => {
  isEditMode.value = true;
  editingId.value = item.id;
  form.name = item.name;
  form.client_key = item.client_key;
  // Resolve course IDs to objects for the multiple lookup component
  form.course_ids = (item.course_ids || [])
    .map(id => allCourses.value.find(c => c.id === id))
    .filter(Boolean);
  form.course_prices = item.course_prices && typeof item.course_prices === 'object' ? { ...item.course_prices } : {};
  form.domains = Array.isArray(item.domains) ? item.domains.join(', ') : '';
  form.active = item.active || 'Y';
  errors.name = '';
  errors.client_key = '';
  errors.domains = '';
  isDialogOpen.value = true;
};

/**
 * Handle form submission (Create or Edit).
 */
const onSubmit = async () => {
  // Validate
  errors.name = '';
  errors.client_key = '';
  errors.domains = '';

  let hasError = false;
  if (!form.name.trim()) {
    errors.name = globalTranslate('externalLmsClientKeys.messages.nameRequired');
    hasError = true;
  }
  if (!isEditMode.value) {
    if (!form.client_key.trim()) {
      errors.client_key = globalTranslate('externalLmsClientKeys.messages.keyRequired');
      hasError = true;
    } else if (form.client_key.trim().length < 10) {
      errors.client_key = globalTranslate('externalLmsClientKeys.messages.keyMinLength');
      hasError = true;
    }
  }

  if (!form.domains.trim()) {
    errors.domains = globalTranslate('externalLmsClientKeys.messages.domainsRequired');
    hasError = true;
  } else {
    const domainList = form.domains.split(',').map(d => d.trim()).filter(Boolean);
    const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    const invalidDomains = domainList.filter(d => !domainRegex.test(d));
    if (invalidDomains.length > 0) {
      errors.domains = globalTranslate('externalLmsClientKeys.messages.invalidDomain');
      hasError = true;
    }
  }

  if (hasError) return;

  isSubmitting.value = true;
  try {
    const courseIds = (form.course_ids || []).map(c => (typeof c === 'object' ? c.id : c));

    const payload = {
      name: form.name.trim(),
      course_ids: courseIds,
      course_prices: form.course_prices,
      domains: form.domains.trim(),
    };

    if (isEditMode.value) {
      payload.active = form.active;
      await axios.put(`/api/v2/admin/external-lms/client-keys/${editingId.value}`, payload);
      showNotification(globalTranslate('externalLmsClientKeys.messages.updateSuccess'));
    } else {
      payload.client_key = form.client_key.trim();
      await axios.post('/api/v2/admin/external-lms/client-keys', payload);
      showNotification(globalTranslate('externalLmsClientKeys.messages.createSuccess'));
      queryParams.page = 1;
      queryParams.search = ''; // Clear search on create to show the new item
    }

    isDialogOpen.value = false;
    await fetchClientKeys();
  } catch (e) {
    const defaultMsg = isEditMode.value
      ? globalTranslate('externalLmsClientKeys.messages.updateFailed')
      : globalTranslate('externalLmsClientKeys.messages.createFailed');

    if (e.response?.status === 422 && e.response?.data?.errors) {
      const apiErrors = e.response.data.errors;
      if (apiErrors.name) [errors.name] = apiErrors.name;
      if (apiErrors.client_key) [errors.client_key] = apiErrors.client_key;
      if (apiErrors.domains) [errors.domains] = apiErrors.domains;

      return;
    }

    showNotification(e.response?.data?.message ?? defaultMsg, 'error');
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(async () => {
  // Load courses for labels
  allCourses.value = await lookupService.getCourses(null);
  await fetchClientKeys();
});
</script>

<style module>
.container {
  position: relative;
  /* Avoid hide Operations menu */
  z-index: 0;
}

.headerCard {
  margin-bottom: 20px;
  padding: 16px;
}

.headerWrapper {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.searchWrapper {
  margin-right: 16px;
  min-width: 350px;
}

.nameColumn {
  font-weight: 500;
  color: var(--v-theme-on-surface);
}

.clientKeyCode {
  padding: 2px 6px;
  background-color: #f5f5f5;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.9em;
}

.courseLookupView {
  min-width: 200px;
}

.courseLookupEdit :global(.v-select--multiple) {
  min-width: 100%;
}

.coursePriceRow {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.coursePriceLabel {
  flex: 1;
  font-size: 0.9em;
  color: var(--v-theme-on-surface);
}

.coursePriceInput {
  width: 130px;
  flex-shrink: 0;
}

/* Modal Styling */
.modalCard {
  border-radius: 12px;
  overflow: hidden;
}

.modalHeader {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: white;
  padding: 1.25rem 1.5rem;
  font-weight: 600;
}

</style>
